import { useCallback, useMemo, type ChangeEvent, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { tv } from 'tailwind-variants';
import {
  AssetBaseInfo,
  FormFieldSearchableSelect,
  NumberInput,
} from '@/shared/components';
import { NUMERIC_CONFIG } from '@/shared/config';
import {
  EXCHANGE_SECTION_TYPES,
  type ExchangeAsset,
  type ExchangeAssetSelectItem,
  type ExchangeInputSectionType,
} from '../../model/types';
import { ExchangeAssetOption } from '../ExchangeAssetOption';
import { ExchangeQuickSelectSection } from '../ExchangeQuickSelectSection';
import { ExchangeQuickSelectSkeleton } from './ExchangeQuickSelectSkeleton';

type Props = {
  type: ExchangeInputSectionType;
  title: string;
  titleContent?: ReactNode;
  headerContent?: ReactNode;
  description?: ReactNode;
  error: string | null;
  isAmountError: boolean;
  isSelectError: boolean;
  amount: string;
  setAmount: (amount: string) => void;
  onAmountBlur: () => void;
  assetsOptions: Array<ExchangeAsset>;
  selectedAsset?: ExchangeAsset;
  onAssetChange: (code: string) => void;
  onQuickSelect: (code: string) => void;
  resetFormErrors: () => void;
};

const fieldStyles = tv({
  slots: {
    section: 'relative flex flex-col gap-[1.2rem]',
    header:
      'flex flex-wrap items-center justify-between gap-x-[1.6rem] gap-y-[0.8rem] min-h-[2.4rem]',
    headerLeft: 'contents tablet:flex tablet:flex-col tablet:gap-[0.4rem]',
    titleRow: 'flex items-center gap-[0.6rem]',
    title: 'medium5 text-foreground',
    headerRight: 'flex flex-wrap items-center justify-end gap-[0.8rem]',
    descriptionMobile: 'tablet:hidden',
    descriptionDesktop: 'hidden tablet:block',
    card: 'flex items-center gap-[0.8rem] h-[6.8rem] rounded-[1.6rem] bg-background ps-[1.2rem] pe-[1.6rem] ring-1 ring-inset ring-stroke shadow-card transition-colors',
    amountInput:
      'hero5 text-foreground placeholder:text-foreground bg-transparent hover:bg-secondary-bg border-none outline-none focus:outline-none flex-1 min-w-0 h-[4.4rem] ps-[1.2rem] pe-0 py-0 rounded-[1rem] cursor-pointer transition-colors',
    selector:
      'h-[3.6rem] w-auto shrink-0 gap-[0.8rem] bg-transparent hover:bg-secondary-bg ps-[1.2rem] pe-[0.8rem] rounded-[1.2rem] headline5 text-foreground border-transparent focus-visible:border-transparent focus-visible:ring-0 [&_label]:text-[1.4rem]! [&_label]:leading-[2rem]! [&_label]:font-semibold! [&_label]:text-foreground [&_svg]:size-[1.6rem] [&_svg]:text-foreground',
    selectorContent:
      'rounded-[2.4rem] w-[35rem] max-w-[calc(100vw-3.2rem)] max-h-[min(44rem,var(--radix-dropdown-menu-content-available-height))]',
    errorText: 'absolute top-[calc(100%+0.8rem)] left-0 p5 text-destructive',
  },
  variants: {
    hasSelectedAsset: {
      true: {
        selector: 'ps-[0.8rem] tablet:ps-[0.4rem]',
      },
    },
    hasDescription: {
      true: {
        titleRow: 'basis-full tablet:basis-auto',
      },
    },
    isAmountError: {
      true: {
        card: 'ring-destructive',
        amountInput: 'text-destructive',
      },
    },
    isSelectError: {
      true: {
        card: 'ring-destructive',
        selector: 'text-destructive',
      },
    },
  },
});

const ExchangeSectionField = ({
  type,
  title,
  titleContent,
  headerContent,
  description,
  error,
  isAmountError,
  isSelectError,
  amount,
  setAmount,
  onAmountBlur,
  assetsOptions,
  selectedAsset,
  onAssetChange,
  onQuickSelect,
  resetFormErrors,
}: Props) => {
  const { t } = useTranslation();
  const styles = fieldStyles({
    isAmountError,
    isSelectError,
    hasSelectedAsset: !!selectedAsset,
    hasDescription: !!description,
  });

  const selectItems = useMemo(
    () =>
      assetsOptions.map(asset => ({
        ...asset,
        value: asset.code,
      })),
    [assetsOptions]
  );

  const selectedAssetMap = useMemo(() => {
    const map = new Map<string, ExchangeAsset>();
    assetsOptions.forEach(asset => map.set(asset.code, asset));
    return map;
  }, [assetsOptions]);

  const renderItem = useCallback(
    (item: ExchangeAssetSelectItem) => (
      <ExchangeAssetOption type={type} asset={item} />
    ),
    [type]
  );

  const renderSelectedValue = useCallback(
    (value: string) => (
      <AssetBaseInfo
        showName={false}
        asset={selectedAssetMap.get(value)}
        className="gap-[0.8rem]"
        iconClassName="size-[2.8rem]"
        labelClassName="headline5"
      />
    ),
    [selectedAssetMap]
  );

  const searchFilter = useCallback(
    ({ code, name }: ExchangeAssetSelectItem, search: string) =>
      [code, name].some(value =>
        value.toLowerCase().includes(search.toLowerCase())
      ),
    []
  );

  const isOptionSelected = useCallback(
    (item: ExchangeAssetSelectItem, value: string) => item.code === value,
    []
  );

  const handleAssetChange = useCallback(
    (code: string) => {
      resetFormErrors();
      onAssetChange(code);
    },
    [resetFormErrors, onAssetChange]
  );

  const handleAmountChange = (e: ChangeEvent<HTMLInputElement>) => {
    setAmount(e.target.value);
  };

  return (
    <section className={styles.section()}>
      <div className={styles.header()}>
        <div className={styles.headerLeft()}>
          <div className={styles.titleRow()}>
            <span className={styles.title()}>{title}</span>
            {titleContent}
          </div>
          {description && (
            <div className={styles.descriptionMobile()}>{description}</div>
          )}
        </div>
        <div className={styles.headerRight()}>
          {selectedAsset ? (
            <>
              {headerContent}
              {description && (
                <div className={styles.descriptionDesktop()}>{description}</div>
              )}
            </>
          ) : (
            <ExchangeQuickSelectSection
              assetsOptions={assetsOptions}
              type={type}
              onSelect={onQuickSelect}
              loadingFallback={<ExchangeQuickSelectSkeleton />}
            />
          )}
        </div>
      </div>

      <div className={styles.card()}>
        <NumberInput
          value={amount}
          onChange={handleAmountChange}
          onBlur={onAmountBlur}
          placeholder="0"
          className={styles.amountInput()}
          decimalScale={
            selectedAsset?.precision ?? NUMERIC_CONFIG.precision.default
          }
        />
        <FormFieldSearchableSelect<ExchangeAssetSelectItem>
          name={type === EXCHANGE_SECTION_TYPES.FROM ? 'assetFrom' : 'assetTo'}
          placeholder={t('common.select')}
          items={selectItems}
          renderItem={renderItem}
          renderSelectedValue={renderSelectedValue}
          className={styles.selector()}
          contentClassName={styles.selectorContent()}
          contentViewportClassName={styles.selectorContent()}
          contentAlign="end"
          searchFilter={searchFilter}
          onValueChange={handleAssetChange}
          withCancelButton={true}
          getItemKey={item => type + item.code}
          isOptionSelected={isOptionSelected}
        />
      </div>

      {error && <span className={styles.errorText()}>{error}</span>}
    </section>
  );
};

export { ExchangeSectionField };
